import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAccountStore } from "../../account/accountStore";
import { collectDevice } from "../../feedback/device";
import {
  FEEDBACK_CATEGORIES,
  FEEDBACK_TEXT_MAX,
  canPreview,
  feedbackOutcome,
  gameIdFromPath,
  localIsoTime,
  previewRows,
  textCounter,
  type FeedbackCategory,
  type FeedbackOutcome,
  type FeedbackRequest,
} from "../../feedback/feedback";
import { newClientId, submitFeedback } from "../../feedback/outbox";
import { APP_ENV, APP_VERSION } from "../../version";
import { routePattern } from "../routes";

/**
 * Sending feedback about the game, opened from the bug button (FeedbackButton).
 *
 * Two steps, and the second is the point: Preview lists EVERYTHING the request
 * will carry - the device details included, and the two things the server adds
 * from the connection - so a player never sends something they have not seen.
 * Edit goes back with the words intact; Cancel closes.
 *
 * A sibling modal on `.confirm-overlay` like ReportDialog, but portalled to the
 * body by FeedbackButton: it is opened from inside the table's HUD strip, whose
 * own stacking context would otherwise put it under the table's overlays.
 *
 * Send goes through the outbox (feedback/outbox.ts): saved on this device
 * first, so no connection only ever means "sent later".
 *
 * The words outlive the dialog until they are sent (`draft`): an accidental tap
 * outside the panel closes it, and the paragraph somebody just wrote about a bug
 * is the one thing here that cannot be recollected.
 */

let draft: { category: FeedbackCategory | null; text: string } = { category: null, text: "" };

export function FeedbackDialog({ onClose }: { onClose: () => void }) {
  const panelRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const uid = useAccountStore((s) => s.uid);
  const signedIn = useAccountStore((s) => s.status === "signedIn");
  const [category, setCategory] = useState<FeedbackCategory | null>(draft.category);
  const [text, setText] = useState(draft.text);
  const [request, setRequest] = useState<FeedbackRequest | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<FeedbackOutcome | null>(null);

  useEffect(() => {
    draft = { category, text };
  }, [category, text]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !sending) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, sending]);

  // On open only. Tied to the effect above, it re-ran whenever onClose or
  // `sending` changed and took focus out of the textarea mid-sentence.
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  async function preview() {
    if (!category || preparing || !canPreview(category, text)) return;
    setPreparing(true);
    const now = new Date();
    const client = await collectDevice();
    setRequest({
      clientId: newClientId(),
      category,
      text: text.trim(),
      clientVersion: APP_VERSION,
      appEnv: APP_ENV,
      screen: routePattern(location.pathname),
      gameId: gameIdFromPath(location.pathname),
      clientTime: localIsoTime(now),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? "",
      client,
    });
    setOutcome(null);
    setPreparing(false);
  }

  function send() {
    if (!request || sending) return;
    setSending(true);
    setOutcome(null);
    // Saved to this device's outbox before the network is tried, so a failure
    // here is "sent later", not "lost" (feedback/outbox.ts).
    void submitFeedback(request)
      .then((result) => {
        const next = feedbackOutcome(result);
        // Sent or safely queued: the draft has done its job.
        if (next.kind === "sent" || next.kind === "queued") draft = { category: null, text: "" };
        setOutcome(next);
      })
      .catch(() => setOutcome({ kind: "error", message: "Something went wrong sending your feedback." }))
      .finally(() => setSending(false));
  }

  // Sent or saved for later: one line and Done. A refusal stays on the preview
  // so Edit can fix it; a failure to even save stays there with Try again.
  const final = outcome && (outcome.kind === "sent" || outcome.kind === "queued");

  return (
    <div className="confirm-overlay" onClick={sending ? undefined : onClose}>
      <div
        ref={panelRef}
        className="card-panel col confirm-panel report-panel feedback-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-title"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="feedback-title" className="title confirm-title">
          {request && !final ? "Check and send" : "Send feedback"}
        </h2>

        {final ? (
          <>
            <div className="subtitle" style={{ textAlign: "center" }}>
              {outcome.kind === "sent" ? "Thanks — we read every one." : outcome.message}
            </div>
            <div className="row confirm-actions">
              <button className="primary" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        ) : request ? (
          <>
            <div className="subtitle subtle">This is everything that will be sent.</div>
            <dl className="feedback-preview">
              {previewRows(request, { playerId: uid ?? "", signedIn }, navigator.userAgent).map((row) => (
                <div key={row.label} className="feedback-preview-row">
                  <dt>{row.label}</dt>
                  <dd className={row.label === "Message" ? "feedback-preview-message" : undefined}>{row.value}</dd>
                </div>
              ))}
            </dl>
            {(outcome?.kind === "error" || outcome?.kind === "rejected") && (
              <div style={{ color: "var(--danger)", textAlign: "center" }}>{outcome.message}</div>
            )}
            <div className="row confirm-actions">
              <button onClick={onClose} disabled={sending}>
                Cancel
              </button>
              <button onClick={() => setRequest(null)} disabled={sending}>
                Edit
              </button>
              <button className="primary" onClick={send} disabled={sending || outcome?.kind === "rejected"}>
                {sending ? "Sending…" : outcome?.kind === "error" ? "Try again" : "Send"}
              </button>
            </div>
          </>
        ) : (
          <>
            <fieldset className="report-categories feedback-categories">
              <legend className="section-label">What's it about?</legend>
              {FEEDBACK_CATEGORIES.map((c) => (
                <label key={c.id} className="row report-category">
                  <input
                    type="radio"
                    name="feedback-category"
                    value={c.id}
                    checked={category === c.id}
                    onChange={() => setCategory(c.id)}
                  />
                  {c.label}
                </label>
              ))}
            </fieldset>
            <label className="col report-details-label">
              <span className="section-label">Tell us</span>
              <textarea
                className="report-details"
                rows={4}
                maxLength={FEEDBACK_TEXT_MAX}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              <span className="report-counter">{textCounter(text)}</span>
            </label>
            <div className="row confirm-actions">
              <button onClick={onClose}>Cancel</button>
              <button className="primary" onClick={() => void preview()} disabled={preparing || !canPreview(category, text)}>
                Preview
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
